'use client'

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { useAuthContext } from '../contexts/AuthContext'
import { useMyLeagueScores, useMyRaffleWinnings } from '../hooks/useFootball'
import { authApi, gameApi, walletApi } from '../api/endpoints'
import LuckyIcon from '../components/lucky/LuckyIcon'
import CoinGlyph from '../components/lucky/CoinGlyph'
import { FriendsGlyph, RankGlyph, TrophyGlyph } from '../components/lucky/ProfileGlyphs'
import {
  buildReferralInviteLink,
  DEFAULT_TELEGRAM_BOT_USERNAME,
} from '../lib/telegramDeepLink'
import {
  coinNameKey,
  copyToClipboard,
  displayUserPublicId,
  formatNumber,
} from '../utils/helpers'
import { COIN_TYPES } from '../types'
import { useLocale, useT } from '../contexts/LocaleContext'
import { LOCALE_META } from '../i18n'
import LanguagePicker from '../components/lucky/LanguagePicker'

const STAGE_STYLES = [
  { num: 'linear-gradient(145deg,#ffe08a,#f0b429)', bar: 'linear-gradient(90deg,#4dff9a,#1fd36a)' },
  { num: 'linear-gradient(145deg,#7eb6ff,#2a6de8)', bar: 'linear-gradient(90deg,#7eb6ff,#2a6de8)' },
  { num: 'linear-gradient(145deg,#ffb075,#e67e22)', bar: 'linear-gradient(90deg,#ffb075,#e67e22)' },
  { num: 'linear-gradient(145deg,#d4b8ff,#8a5cff)', bar: 'linear-gradient(90deg,#d4b8ff,#8a5cff)' },
]

export default function ProfilePage() {
  const navigate = useNavigate()
  const t = useT()
  const { locale } = useLocale()
  const numberLocale = LOCALE_META[locale].numberLocale
  const { user } = useAuthContext()
  const [copied, setCopied] = useState(false)
  const [copiedId, setCopiedId] = useState(false)
  const [botUsername, setBotUsername] = useState(DEFAULT_TELEGRAM_BOT_USERNAME)

  const drawTypeLabel = (drawType: string) => {
    const key = `football.draws.${drawType}`
    const label = t(key)
    return label === key ? drawType : label
  }

  const inviteLink = useMemo(
    () => (user?.referral_code ? buildReferralInviteLink(botUsername, user.referral_code) : ''),
    [botUsername, user?.referral_code],
  )

  const { data: myScoresData } = useMyLeagueScores()
  const myScores = myScoresData?.scores || []
  const totalFootballScore = myScores.reduce((sum, s) => sum + s.total_score, 0)

  const { data: winningsData } = useMyRaffleWinnings()
  const winnings = winningsData?.winnings || []
  const { data: walletData } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletApi.getCoins(),
  })
  const { data: luckyStatus } = useQuery({
    queryKey: ['lucky-numbers-status'],
    queryFn: () => gameApi.getLuckyNumbersStatus(),
  })

  const coins = {
    diamond: walletData?.coins?.diamond ?? user?.wallet?.coins?.diamond ?? 0,
    gold: walletData?.coins?.gold ?? user?.wallet?.coins?.gold ?? 0,
    silver: walletData?.coins?.silver ?? user?.wallet?.coins?.silver ?? 0,
    bronze: walletData?.coins?.bronze ?? user?.wallet?.coins?.bronze ?? 0,
  }

  const totalCoins = coins.bronze + coins.silver + coins.gold + coins.diamond
  const bestCoin =
    coins.diamond > 0
      ? t('coins.diamond')
      : coins.gold > 0
        ? t('coins.gold')
        : coins.silver > 0
          ? t('coins.silver')
          : coins.bronze > 0
            ? t('coins.bronze')
            : '—'

  const badges = useMemo(
    () =>
      [
        {
          key: 'first',
          label: t('profile.badgeFirst'),
          kind: 'coin' as const,
          coin: 'gold' as const,
          on: totalCoins > 0,
        },
        {
          key: 'invite',
          label: t('profile.badgeInvite'),
          kind: 'friends' as const,
          on: (user?.referral_count ?? 0) >= 5,
        },
        {
          key: 'lucky',
          label: t('profile.badgeLucky'),
          kind: 'coin' as const,
          coin: 'diamond' as const,
          on: (luckyStatus?.current_stage ?? 1) >= 3 || !!luckyStatus?.completed,
        },
      ] as const,
    [t, totalCoins, user?.referral_count, luckyStatus?.current_stage, luckyStatus?.completed],
  )

  const stageProgress = useMemo(() => {
    const current = luckyStatus?.completed ? 5 : luckyStatus?.current_stage ?? 1
    return [1, 2, 3, 4].map((stage) => {
      if (current > stage) return 100
      if (current === stage) return 45
      return 0
    })
  }, [luckyStatus?.current_stage, luckyStatus?.completed])

  useEffect(() => {
    authApi
      .getRandomReferralCode()
      .then((res) => {
        if (res.bot_username) setBotUsername(res.bot_username.replace(/^@/, ''))
      })
      .catch(() => {})
  }, [])

  const handleCopyCode = () => {
    if (user?.referral_code) {
      copyToClipboard(user.referral_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleCopyTransferId = () => {
    const publicId = displayUserPublicId(user)
    if (!publicId) return
    copyToClipboard(publicId)
    setCopiedId(true)
    setTimeout(() => setCopiedId(false), 2000)
  }

  return (
    <div className="lucky-profile mini-app-container pb-20">
      <div className="p-4 pt-14">
        <LanguagePicker />

        {user && (
          <section className="lucky-profile__card">
            <div className="lucky-profile__hero">
              {user.photo_url ? (
                <img src={user.photo_url} alt={user.first_name} className="lucky-profile__avatar" />
              ) : (
                <LuckyIcon name="profile" size={64} className="lucky-profile__avatar" alt={t('nav.profile')} />
              )}
              <div className="min-w-0 flex-1">
                <h1 className="lucky-profile__name">
                  {user.first_name} {user.last_name}
                </h1>
                <p className="lucky-profile__sub">@{user.username || t('common.noName')}</p>
                <button
                  type="button"
                  className="lucky-profile__id-chip"
                  onClick={handleCopyTransferId}
                  title={t('profile.idCopyTitle')}
                  dir="ltr"
                >
                  <span className="lucky-profile__id-label">{t('profile.idLabel')}</span>
                  <strong>{displayUserPublicId(user) || '—'}</strong>
                  <span className="lucky-profile__id-copy">
                    {copiedId ? t('common.copied') : t('common.copy')}
                  </span>
                </button>
              </div>
            </div>

            <div className="lucky-profile__stats">
              <div className="lucky-profile__stat">
                <FriendsGlyph size={30} />
                <strong>{formatNumber(user.referral_count, numberLocale)}</strong>
                <span>{t('profile.invites')}</span>
              </div>
              <div className="lucky-profile__stat">
                <RankGlyph size={30} />
                <strong>{formatNumber(user.level, numberLocale)}</strong>
                <span>{t('profile.level')}</span>
              </div>
              <div className="lucky-profile__stat">
                <CoinGlyph
                  type={
                    coins.diamond > 0
                      ? 'diamond'
                      : coins.gold > 0
                        ? 'gold'
                        : coins.silver > 0
                          ? 'silver'
                          : 'bronze'
                  }
                  size={28}
                />
                <strong>{bestCoin}</strong>
                <span>{t('profile.bestCoin')}</span>
              </div>
            </div>

            <div className="lucky-profile__coins">
              {COIN_TYPES.map((type) => (
                <div key={type} className="lucky-profile__coin">
                  <CoinGlyph type={type} size={34} className="ico" />
                  <div className="val">{formatNumber(coins[type] ?? 0, numberLocale)}</div>
                  <div className="lbl">{t(coinNameKey(type))}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="lucky-profile__card">
          <h2 className="lucky-profile__title">
            <TrophyGlyph size={22} /> {t('profile.achievements')}
          </h2>
          <div className="lucky-profile__badges">
            {badges.map((b) => (
              <div key={b.key} className={`lucky-profile__badge ${b.on ? 'is-on' : ''}`}>
                {b.kind === 'coin' ? (
                  <CoinGlyph type={b.coin} size={52} className="lucky-profile__badge-icon" />
                ) : (
                  <FriendsGlyph size={52} className="lucky-profile__badge-icon" />
                )}
                <p>{b.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="lucky-profile__card">
          <h2 className="lucky-profile__title">
            <LuckyIcon name="game" size={22} /> {t('profile.luckyProgress')}
          </h2>
          <div className="lucky-profile__progress">
            {STAGE_STYLES.map((style, i) => {
              const pct = stageProgress[i]
              return (
                <div key={i} className="lucky-profile__progress-row">
                  <div className="lucky-profile__progress-num" style={{ background: style.num }}>
                    {i + 1}
                  </div>
                  <div className="lucky-profile__bar">
                    <i style={{ width: `${pct}%`, background: style.bar }} />
                  </div>
                  <span className="lucky-profile__pct">{pct}%</span>
                </div>
              )
            })}
          </div>
        </section>

        <section className="lucky-profile__card">
          <div className="lucky-profile__section-head">
            <h2>
              <LuckyIcon name="challenge" size={22} /> {t('profile.footballScore')}
            </h2>
            <strong>{formatNumber(totalFootballScore, numberLocale)}</strong>
          </div>
          <p className="lucky-profile__sub" style={{ marginBottom: 10 }}>
            {t('profile.footballScoreHint')}
          </p>
          {myScores.length === 0 ? (
            <p className="lucky-profile__empty">{t('profile.noLeagueYet')}</p>
          ) : (
            <div className="lucky-profile__list">
              {myScores.map((s) => (
                <div key={s.league_id} className="lucky-profile__row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm">
                      {s.mode === 'big_match' ? t('football.modeBig') : t('football.dailyLeague')}
                    </span>
                    <span
                      className={`lucky-profile__tag ${
                        s.mode === 'league' ? 'lucky-profile__tag--league' : 'lucky-profile__tag--big'
                      }`}
                    >
                      {s.mode === 'league' ? t('common.league') : t('common.bigMatch')}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center text-xs">
                    <div>
                      <p className="font-bold text-yellow-300 text-sm">{s.total_score}</p>
                      <p className="text-[#8fa8d4]">{t('common.all')}</p>
                    </div>
                    <div>
                      <p className="font-bold text-green-300 text-sm">{s.daily_score}</p>
                      <p className="text-[#8fa8d4]">{t('common.daily')}</p>
                    </div>
                    <div>
                      <p className="font-bold text-purple-300 text-sm">{s.monthly_score}</p>
                      <p className="text-[#8fa8d4]">{t('common.monthly')}</p>
                    </div>
                    <div>
                      <p className="font-bold text-sm">{s.rank > 0 ? `#${s.rank}` : '-'}</p>
                      <p className="text-[#8fa8d4]">{t('common.rank')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {winnings.length > 0 && (
          <section className="lucky-profile__card">
            <h2 className="lucky-profile__title">
              <LuckyIcon name="gift" size={22} /> {t('profile.footballPrizes')}
            </h2>
            <div className="lucky-profile__list">
              {winnings.map((w) => (
                <div key={w.id} className="lucky-profile__row justify-between">
                  <span className="flex items-center gap-2 text-sm">
                    <LuckyIcon name="chest" size={28} />
                    <span>{drawTypeLabel(w.draw_type)}</span>
                  </span>
                  <span className="text-xs text-[#8fa8d4]">{w.awarded_at}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {user && (
          <section className="lucky-profile__card text-center">
            <h2 className="lucky-profile__title">
              <FriendsGlyph size={22} /> {t('profile.inviteFriends')}
            </h2>
            <p className="lucky-profile__sub">{t('profile.inviteHint')}</p>
            <div className="lucky-profile__ref-box">
              <button
                type="button"
                className="lucky-profile__ref-code"
                onClick={handleCopyCode}
                disabled={!user.referral_code}
                title={t('common.copy')}
              >
                <code>{user.referral_code || '—'}</code>
                <span className="lucky-profile__ref-copy">
                  {copied ? (
                    <>
                      <LuckyIcon name="confirm" size={16} /> {t('common.copied')}
                    </>
                  ) : (
                    t('common.copy')
                  )}
                </span>
              </button>
            </div>
            {inviteLink && (
              <div className="lucky-profile__qr">
                <div className="lucky-profile__qr-frame" aria-label={t('profile.qrLabel')}>
                  <QRCodeSVG
                    value={inviteLink}
                    size={168}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#0b1a33"
                    marginSize={1}
                  />
                </div>
                <p className="lucky-profile__qr-hint">{t('profile.qrHint')}</p>
              </div>
            )}
          </section>
        )}

        <section className="lucky-profile__card">
          <h2 className="lucky-profile__title">
            <TrophyGlyph size={22} /> {t('profile.topUsers')}
          </h2>
          <p className="lucky-profile__sub" style={{ marginBottom: 12 }}>
            {t('profile.topUsersHint')}
          </p>
          <button type="button" className="lucky-profile__link-btn" onClick={() => navigate('/top-users')}>
            {t('profile.viewTopUsers')}
          </button>
        </section>
      </div>
    </div>
  )
}
